import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { Check, X, Loader2, CheckCircle } from 'lucide-react';

const Pricing = () => {
  const navigate = useNavigate();
  const { token, isCalendarAdmin, getAuthHeaders, runningUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/api/plans/`);
      if (!response.ok) {
        throw new Error('Failed to fetch plans');
      }
      const data = await response.json();
      // Sort by price
      const sortedPlans = data.sort((a, b) => a.price - b.price);
      setPlans(sortedPlans);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching plans:', err);
      setLoading(false);
    }
  };

  const handlePlanClick = (plan) => {
    setSelectedPlan(plan);
    setShowDetailModal(true);
    setError('');
  };

  const handleSubscribe = async () => {
    setError('');
    
    // Check if user is logged in
    if (!user || !token) {
      navigate('/register', { state: { planId: selectedPlan?.id, from: '/pricing' } });
      return;
    }
    
    // Check if user is a calendar admin
    if (!isCalendarAdmin || !isCalendarAdmin()) {
      setError('Only Calendar Admins can subscribe to plans. Please register as a Calendar Admin first.');
      return;
    }
    
    // Check if already on this plan
    if (user?.plan_type?.toLowerCase() === selectedPlan?.name?.toLowerCase()) {
      setError(`You are already on the ${selectedPlan?.name} plan.`);
      return;
    }
    
    try {
      setCheckoutLoading(true);
      
      // Map plan name to plan_id for the API
      const planId = selectedPlan?.name?.toLowerCase();
      
      const response = await axios.post(
        `${API_URL}/api/payments/checkout`,
        {
          plan_id: planId,
          origin_url: window.location.origin
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        if (response.data.data.checkout_url) {
          const checkoutUrl = response.data.data.checkout_url;
          
          // Detect if running inside an iframe (e.g., preview environment)
          const isInIframe = window.self !== window.top;
          
          if (isInIframe) {
            // Open in new tab for iframe environments (Stripe doesn't work in iframes)
            window.open(checkoutUrl, '_blank');
          } else {
            // Direct redirect for normal browser environment
            window.location.href = checkoutUrl;
          }
        } else {
          // Free plan - already activated
          setShowDetailModal(false);
          navigate('/dashboard');
        }
      }
    } catch (err) {
      console.error('Error creating checkout:', err);
      setError(err.response?.data?.detail || 'Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const isCurrentPlan = (planName) => {
    return user?.plan_type?.toLowerCase() === planName?.toLowerCase();
  };

  const isPopular = (index) => {
    // Mark the second plan (index 1) as popular, or adjust based on plan count
    return index === 1 || (plans.length === 2 && index === 1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex justify-center items-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
          <span className="ml-3 text-gray-600">Loading plans...</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Simple, Transparent Pricing</h1>
            <p className="text-gray-600">Choose the plan that works for your business</p>
            {runningUser?.plan_type && (
              <div className="mt-4">
                <span className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  <Check className="w-4 h-4 mr-2" />
                  Current Plan: {runningUser.plan_type.charAt(0).toUpperCase() + runningUser.plan_type.slice(1)}
                </span>
              </div>
            )}
          </div>

          {/* Plans Grid - Matching ManageAccount style */}
          {plans.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No plans available at the moment.
            </div>
          ) : (
            <div className={`grid grid-cols-1 gap-6 ${
              plans.length === 2 ? 'md:grid-cols-2' :
              plans.length === 3 ? 'md:grid-cols-3' :
              plans.length >= 4 ? 'md:grid-cols-4' : ''
            }`}>
              {plans.map((plan, index) => {
                const popular = isPopular(index);
                const isCurrent = isCurrentPlan(plan.name);
                
                return (
                  <div
                    key={plan.id}
                    data-testid={`pricing-plan-${plan.name.toLowerCase()}`}
                    className={`bg-white rounded-lg shadow-md p-6 relative cursor-pointer transition-all duration-200 hover:shadow-lg ${
                      popular ? 'ring-2 ring-yellow-500' : ''
                    } ${isCurrent ? 'ring-2 ring-green-500' : ''}`}
                    onClick={() => handlePlanClick(plan)}
                  >
                    {/* Popular Badge - Corner style like ManageAccount */}
                    {popular && !isCurrent && (
                      <div className="absolute top-0 right-0 bg-yellow-500 text-white px-3 py-1 rounded-bl-lg rounded-tr-lg text-sm font-semibold">
                        Popular
                      </div>
                    )}
                    
                    {/* Current Badge - Corner style like ManageAccount */}
                    {isCurrent && (
                      <div className="absolute top-0 left-0 bg-green-500 text-white px-3 py-1 rounded-br-lg rounded-tl-lg text-sm font-semibold flex items-center">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Current
                      </div>
                    )}
                    
                    {/* Plan Content */}
                    <div className="mb-6 mt-4">
                      <h3 className="text-2xl font-bold text-gray-800 mb-2">{plan.name}</h3>
                      <div className="flex items-baseline">
                        <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                        <span className="text-gray-600 ml-2">/month</span>
                      </div>
                      {plan.description && (
                        <p className="text-sm text-gray-500 mt-2">{plan.description}</p>
                      )}
                    </div>
                    
                    {/* Features List - Show 6 like ManageAccount */}
                    <ul className="space-y-3 mb-6">
                      {plan.display_features?.slice(0, 6).map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600 text-sm">{feature}</span>
                        </li>
                      ))}
                      {plan.display_features?.length > 6 && (
                        <li className="text-yellow-600 text-sm font-medium">
                          +{plan.display_features.length - 6} more features
                        </li>
                      )}
                    </ul>
                    
                    {/* Action Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlanClick(plan);
                      }}
                      data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                      className={`w-full py-3 px-4 rounded-md font-semibold transition-colors ${
                        isCurrent
                          ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                          : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                      }`}
                    >
                      {isCurrent ? 'Current Plan' : 'View Details'}
                    </button>
                    
                    {plan.price > 0 && !isCurrent && (
                      <p className="text-xs text-gray-500 text-center mt-2">
                        Secure payment via Stripe
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Payment Security Notice */}
          <div className="mt-8 text-center">
            <p className="text-gray-500 text-sm">
              🔒 Secure payments powered by Stripe. Your payment information is never stored on our servers.
            </p>
          </div>
        </div>
      </section>
      <Footer />

      {/* Plan Detail Modal */}
      {showDetailModal && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-2">{selectedPlan.name}</h2>
                <p className="text-gray-600">{selectedPlan.description}</p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-500 hover:text-gray-700"
                data-testid="close-plan-modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-6">
              <div className="text-4xl font-bold text-yellow-600">
                ${selectedPlan.price}
                <span className="text-lg text-gray-600 font-normal">/month</span>
              </div>
              {selectedPlan.price > 0 && (
                <p className="text-sm text-gray-500 mt-1">Billed monthly. Cancel anytime.</p>
              )}
            </div>

            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Features Included:</h3>
              <ul className="space-y-3">
                {selectedPlan.display_features?.map((feature, idx) => (
                  <li key={idx} className="flex items-start text-gray-700">
                    <CheckCircle className="w-5 h-5 mr-3 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
                {error}
              </div>
            )}

            <button
              onClick={handleSubscribe}
              disabled={checkoutLoading || isCurrentPlan(selectedPlan.name)}
              data-testid="subscribe-btn"
              className={`w-full font-semibold py-4 rounded-md transition-colors flex items-center justify-center ${
                isCurrentPlan(selectedPlan.name)
                  ? 'bg-green-100 text-green-700 cursor-not-allowed'
                  : 'bg-yellow-500 hover:bg-yellow-600 text-white'
              }`}
            >
              {checkoutLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : isCurrentPlan(selectedPlan.name) ? (
                'This is your current plan'
              ) : user ? (
                selectedPlan.price === 0 ? 'ACTIVATE FREE PLAN' : 'SUBSCRIBE NOW'
              ) : (
                'SIGN UP & SUBSCRIBE'
              )}
            </button>
            
            {selectedPlan.price > 0 && (
              <p className="text-center text-sm text-gray-500 mt-4">
                🔒 You&apos;ll be redirected to Stripe for secure payment
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;
